import { RedDotListData, TmapApp } from '@tmap-web-lib/tmap-app-interface'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai/index'
import { debounce } from 'lodash-es'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { globalState } from '@/atoms'
import { sendEventLog } from '@/features/log/log.fn'
import MyLogDefinition from '@/features/log/log.my'
import { useOpenService } from '@/hooks'

import * as style from './vehicleInfo.css'

export interface VehicleInfoProps {
  carNumber: string | null
  vehicleType: string
  fuelType: string
  isParking: boolean | null
  redDotList: RedDotListData[]
}

function VehicleInfo(props: VehicleInfoProps) {
  const { carNumber, vehicleType, fuelType, isParking, redDotList } = props
  const openService = useOpenService()
  const globalStateValue = useAtomValue(globalState)
  const insuranceBanner = globalStateValue.MY_INSURANCE_BANNER
  const vehicleRegisterText = globalStateValue.MY_VEHICLE_REGISTER_TEXT ?? {
    url: 'tmap://carprofile',
    text: '내 차 운전점수로 보험 할인받기',
  }
  const { action_id } = MyLogDefinition.my
  const [isInsuranceBannerShow, setIsInsuranceBannerShow] = useState(false)

  const callClickDebouncing = useMemo(
    () =>
      debounce(
        () => {
          const custom = {
            main_car_number: carNumber ? (carNumber !== '모터바이크' ? 'Y' : 'N') : 'N',
          }
          sendEventLog(action_id?.tapCarprofile, custom)
          openService('tmap://carprofile')
        },
        1000,
        { leading: true, trailing: false }
      ),
    [openService, action_id?.tapCarprofile, carNumber]
  )

  const handleClick = useCallback(() => {
    callClickDebouncing()
  }, [callClickDebouncing])

  const readRedDot = useCallback(() => {
    TmapApp.updateRedDotList({
      updateData: [
        {
          itemGroup: 'SERVICE_INDEX',
          item: 'MY_INSURANCE',
          displayType: 'REDDOT',
          readDateTime: dayjs().format('YYYYMMDDHHmmss'),
        },
      ],
    })

    setIsInsuranceBannerShow(false)
  }, [])

  const callCloseBtnDebouncing = useMemo(
    () =>
      debounce(
        () => {
          readRedDot()
        },
        1000,
        { leading: true, trailing: false }
      ),
    [readRedDot]
  )

  const handleCloseBtn = useCallback(() => {
    callCloseBtnDebouncing()
  }, [callCloseBtnDebouncing])

  const callInsuranceBtnDebouncing = useMemo(
    () =>
      debounce(
        () => {
          readRedDot()

          sendEventLog(action_id?.tapUbiBanner, {
            body_text: insuranceBanner?.bodyText,
            btn_text: insuranceBanner?.btnText,
          })

          if (insuranceBanner?.url) {
            openService(insuranceBanner.url)
          }
        },
        1000,
        { leading: true, trailing: false }
      ),
    [readRedDot, action_id?.tapUbiBanner, openService, insuranceBanner]
  )

  const handleInsuranceButtonClick = useCallback(() => {
    callInsuranceBtnDebouncing()
  }, [callInsuranceBtnDebouncing])

  useEffect(() => {
    if (redDotList.length) {
      setIsInsuranceBannerShow(true)
      sendEventLog(action_id?.viewUbiBanner, {
        body_text: insuranceBanner?.bodyText,
        btn_text: insuranceBanner?.btnText,
      })
    } else {
      setIsInsuranceBannerShow(false)
    }
  }, [redDotList, action_id?.viewUbiBanner, insuranceBanner])

  return (
    <div className={style.vehicleInfoWrapper}>
      <div
        className={style.wrapper}
        data-testid="vehicle_info"
        onClick={handleClick}
      >
        {carNumber ? (
          <>
            <p className={`${style.carInfo} number`}>{carNumber}</p>
          </>
        ) : (
          <div className={style.registerVehicleWrapper}>
            <Image
              className={style.registerVehicleImg}
              src={require('../../../../../public/assets/images/plus_circle.svg').default}
              alt=""
            />
            <p className={`${style.carInfo} text`}>{vehicleRegisterText.text}</p>
          </div>
        )}
        {carNumber ? (
          <div className={style.tagWrapper}>
            {vehicleType ? <div className={style.tags}>{vehicleType}</div> : null}
            {fuelType ? <div className={style.tags}>{fuelType}</div> : null}
            {isParking ? <div className={style.tags}>티맵주차</div> : null}
          </div>
        ) : null}

        <Image
          src={require('../../../../../public/assets/images/ico_arrow_right.svg').default}
          alt=""
        />
      </div>
      {isInsuranceBannerShow ? (
        <div
          className={style.insuranceModule}
          onClick={(e) => {
            e.stopPropagation()
            handleInsuranceButtonClick()
          }}
        >
          <Image
            className={style.infoImage}
            src={require('../../../../assets/images/ic_my_info.svg').default}
            alt=""
          />
          <div className={style.insuranceModuleContent}>
            <p>
              {insuranceBanner?.bodyText}
              <span className={style.insuranceRedDot} />
            </p>
            <p>{insuranceBanner?.btnText}</p>
            <p>{insuranceBanner?.subText}</p>
          </div>

          <button
            className={style.closeBtn}
            onClick={(e) => {
              e.stopPropagation()
              handleCloseBtn()
            }}
          >
            <Image
              src={require('../../../../assets/images/ic_my_close.svg').default}
              alt=""
            />
          </button>
        </div>
      ) : null}
    </div>
  )
}

export { VehicleInfo }
